// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Employees page (HRMS-1 grid + HRMS-2 tabbed editor).
// Server-paged list (unchanged workflow) + a compact tabbed Modal
// covering General / Employment / Sales / Contacts / Documents /
// Login / Manager / Branch / History. RBAC server-side (hrms.employee.*).
// Foundation master only — no payroll / attendance / leave / expense.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Input, Select, Switch, Space, Spin, Table, Tag, Tooltip, Modal, Form,
  DatePicker, InputNumber, Popconfirm, Tabs, Alert, Divider, Dropdown, message
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, TeamOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  MoreOutlined, SolutionOutlined, SwapOutlined, HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { hrEmployeesApi } from './hrEmployeesApi';
import { hrDepartmentsApi } from '../departments/hrDepartmentsApi';
import { hrDesignationsApi } from '../designations/hrDesignationsApi';
import { hrBranchesApi } from '../branches/hrBranchesApi';
import EmployeeSubGrid from './EmployeeSubGrid';
import EmployeeProvisioningModal from './EmployeeProvisioningModal';
import { eventLabel, eventTagColor, formatSnapshot } from '../history/historyHelpers';
import {
  EMPLOYEE_TYPE_OPTIONS, ROLE_TYPE_OPTIONS, STATUS_OPTIONS, GENDER_OPTIONS, MARITAL_OPTIONS,
  DOCUMENT_TYPE_OPTIONS, CONTACT_TYPE_OPTIONS, ADDRESS_TYPE_OPTIONS,
  statusTagColor, isValidIndianMobile, isValidEmail, buildEmployeePayload
} from './hrEmployeesHelpers';
import '../hrms.css';

const PAGE_SIZE = 20;
const toDayjs = (v) => (v ? dayjs(v) : null);
const mobileRule = { validator: (_r, v) => isValidIndianMobile(v) ? Promise.resolve() : Promise.reject(new Error('Enter a valid Indian mobile (10 digits starting 6-9).')) };
const emailRule  = { validator: (_r, v) => isValidEmail(v) ? Promise.resolve() : Promise.reject(new Error('Enter a valid email address.')) };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' };

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [provisionFor, setProvisionFor] = useState(null);       // employee id
  const [changeHistoryFor, setChangeHistoryFor] = useState(null); // employee record
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState(null);

  const [depts, setDepts]   = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [managerOpts, setManagerOpts] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [loadingOne, setLoadingOne] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await hrEmployeesApi.list({
        q: search || undefined, status: status || undefined,
        limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE
      });
      setRows((out && out.rows) || []);
      setTotal((out && out.total) || 0);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load employees.');
    } finally { setLoading(false); }
  }, [search, status, page]);
  useEffect(() => { load(); }, [load]);

  const loadLookups = useCallback(async () => {
    try {
      const [d, g, b, emp] = await Promise.all([
        hrDepartmentsApi.lookups(), hrDesignationsApi.lookups(), hrBranchesApi.lookups(),
        hrEmployeesApi.list({ limit: 1000 })
      ]);
      setDepts(((d && d.rows) || []).map((x) => ({ value: x.id, label: x.departmentName })));
      setDesigs(((g && g.rows) || []).map((x) => ({ value: x.id, label: x.designationName })));
      setBranches(((b && b.rows) || []).map((x) => ({ value: x.id, label: x.branchName })));
      setManagerOpts(((emp && emp.rows) || []).map((x) => ({ value: x.id, label: `${x.displayName || x.employeeName} (${x.employeeCode})` })));
    } catch (_e) { /* soft-fail */ }
  }, []);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  const setFormFromRecord = (rec) => {
    form.setFieldsValue({
      ...rec,
      joiningDate: toDayjs(rec.joiningDate), dateOfBirth: toDayjs(rec.dateOfBirth),
      confirmationDate: toDayjs(rec.confirmationDate), relievingDate: toDayjs(rec.relievingDate),
      departmentEffectiveFrom: toDayjs(rec.departmentEffectiveFrom),
      designationEffectiveFrom: toDayjs(rec.designationEffectiveFrom),
      salespersonSettings: rec.salespersonSettings || {}
    });
  };

  const openAdd = () => {
    setEditing(null); setActiveTab('general');
    form.resetFields();
    form.setFieldsValue({ employeeType: 'PERMANENT', roleType: 'STAFF', status: 'ACTIVE', isSalesperson: false, canLogin: false, loginEnabled: false, loginLocked: false });
    setModalOpen(true);
  };
  const openEdit = async (rec) => {
    setActiveTab('general'); setEditing(rec); setModalOpen(true); setLoadingOne(true);
    form.resetFields();
    try {
      const full = await hrEmployeesApi.get(rec.id);
      setEditing(full);
      setFormFromRecord(full);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load employee.');
    } finally { setLoadingOne(false); }
  };

  const submit = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { message.error('Please fix the highlighted fields.'); return; }
    const payload = buildEmployeePayload(values);
    setSaving(true);
    try {
      if (editing && editing.id) {
        const updated = await hrEmployeesApi.update(editing.id, payload);
        setEditing(updated); setFormFromRecord(updated);
        message.success('Employee updated.');
      } else {
        const created = await hrEmployeesApi.create(payload);
        setEditing(created); setFormFromRecord(created);
        message.success('Employee created. You can now add contacts, documents & addresses.');
      }
      load(); loadLookups();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save employee.');
    } finally { setSaving(false); }
  };

  const remove = async (rec) => {
    try { await hrEmployeesApi.remove(rec.id); message.success('Employee removed.'); load(); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to remove employee.'); }
  };

  const columns = useMemo(() => [
    { title: 'Code', dataIndex: 'employeeCode', width: 110, fixed: 'left' },
    { title: 'Name', dataIndex: 'employeeName', width: 180 },
    { title: 'Display', dataIndex: 'displayName', width: 130 },
    { title: 'Mobile', dataIndex: 'mobile', width: 120 },
    { title: 'Department', dataIndex: 'departmentName', width: 130 },
    { title: 'Designation', dataIndex: 'designationName', width: 150 },
    { title: 'Branch', dataIndex: 'branchName', width: 120, render: (v) => v || '—' },
    { title: 'Manager', dataIndex: 'reportsToName', width: 140, render: (v) => v || '—' },
    { title: 'Type', dataIndex: 'employeeType', width: 100 },
    { title: 'Salesperson?', dataIndex: 'isSalesperson', width: 100, align: 'center',
      render: (v) => v ? <Tag color="blue">YES</Tag> : <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Tag color={statusTagColor(v)}>{v}</Tag> },
    { title: 'Actions', key: 'actions', width: 128, fixed: 'right', align: 'center',
      render: (_v, rec) => (
        <Space size={4}>
          <Tooltip title="Edit"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(rec)} /></Tooltip>
          <Dropdown trigger={['click']} menu={{ items: [
            { key: 'history', icon: <HistoryOutlined />, label: 'View history', onClick: () => setChangeHistoryFor(rec) },
            { key: 'transfer', icon: <SwapOutlined />, label: 'Create transfer',
              onClick: () => navigate('/erp/hrms/employee-transfers', { state: { employeeId: rec.id } }) },
            { key: 'provision', icon: <SolutionOutlined />, label: 'Provision login', onClick: () => setProvisionFor(rec.id) }
          ] }}>
            <Button size="small" type="text" icon={<MoreOutlined />} />
          </Dropdown>
          <Popconfirm title="Remove this employee?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove(rec)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) }
  ], [navigate]);

  const hasId = !!(editing && editing.id);
  const saveFirst = <Alert type="info" showIcon message="Save the employee first, then add these records." style={{ marginBottom: 8 }} />;

  const generalTab = (
    <div style={grid2}>
      <Form.Item name="employeeCode" label="Employee Code" rules={[{ required: true, message: 'Employee Code is required.' }]}><Input maxLength={40} placeholder="e.g., EMP001" /></Form.Item>
      <Form.Item name="employeeName" label="Employee Name" rules={[{ required: true, message: 'Employee Name is required.' }]}><Input maxLength={160} /></Form.Item>
      <Form.Item name="displayName" label="Display Name"><Input maxLength={120} /></Form.Item>
      <Form.Item name="mobile" label="Personal Mobile" rules={[mobileRule]}><Input maxLength={15} /></Form.Item>
      <Form.Item name="email" label="Personal Email" rules={[emailRule]}><Input maxLength={160} /></Form.Item>
      <Form.Item name="gender" label="Gender"><Select allowClear options={GENDER_OPTIONS} /></Form.Item>
      <Form.Item name="dateOfBirth" label="Date of Birth"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
      <Form.Item name="bloodGroup" label="Blood Group"><Input maxLength={8} placeholder="e.g., O+" /></Form.Item>
      <Form.Item name="maritalStatus" label="Marital Status"><Select allowClear options={MARITAL_OPTIONS} /></Form.Item>
      <Form.Item name="aadhaarLast4" label="Aadhaar (last 4)" rules={[{ pattern: /^[0-9]{4}$/, message: 'Exactly 4 digits.', validateTrigger: 'onBlur' }]}><Input maxLength={4} placeholder="####" /></Form.Item>
      <Form.Item name="panLast4" label="PAN (last 4)"><Input maxLength={4} /></Form.Item>
      <Form.Item name="employeePhoto" label="Photo (URL / path)"><Input maxLength={2000} /></Form.Item>
      <Form.Item name="remarks" label="Remarks" style={{ gridColumn: '1 / span 2' }}><Input.TextArea rows={2} maxLength={2000} /></Form.Item>
    </div>
  );

  const employmentTab = (
    <div style={grid2}>
      <Form.Item name="employeeType" label="Category (Type)"><Select options={EMPLOYEE_TYPE_OPTIONS} /></Form.Item>
      <Form.Item name="roleType" label="Role Type"><Select options={ROLE_TYPE_OPTIONS} /></Form.Item>
      <Form.Item name="status" label="Status"><Select options={STATUS_OPTIONS} /></Form.Item>
      <Form.Item name="statusReason" label="Status Reason"><Input maxLength={2000} /></Form.Item>
      <Form.Item name="departmentId" label="Department"><Select allowClear showSearch optionFilterProp="label" options={depts} /></Form.Item>
      <Form.Item name="designationId" label="Designation"><Select allowClear showSearch optionFilterProp="label" options={desigs} /></Form.Item>
      <Form.Item name="joiningDate" label="Joining Date"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
      <Form.Item name="confirmationDate" label="Confirmation Date"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
      <Form.Item name="relievingDate" label="Relieving Date"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
      <Form.Item name="probationMonths" label="Probation (months)"><InputNumber min={0} max={120} style={{ width: '100%' }} /></Form.Item>
      <Form.Item name="departmentEffectiveFrom" label="Dept Effective From"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
      <Form.Item name="designationEffectiveFrom" label="Desig Effective From"><DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" /></Form.Item>
    </div>
  );

  const salesTab = (
    <div>
      <Form.Item name="isSalesperson" label="Is Salesperson?" valuePropName="checked" tooltip="Only active salespersons can be mapped to customers."><Switch /></Form.Item>
      <Divider style={{ margin: '4px 0 12px' }} orientation="left" plain>Salesperson Settings</Divider>
      <div style={grid2}>
        <Form.Item name={['salespersonSettings', 'salespersonCode']} label="Salesperson Code" tooltip="Must be unique across employees."><Input maxLength={40} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'territory']} label="Territory"><Input maxLength={160} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'monthlyTarget']} label="Monthly Target"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'quarterlyTarget']} label="Quarterly Target"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'yearlyTarget']} label="Yearly Target"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'defaultCustomerLimit']} label="Default Customer Limit"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        <Form.Item name={['salespersonSettings', 'commissionEligible']} label="Commission Eligible" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name={['salespersonSettings', 'active']} label="Settings Active" valuePropName="checked" initialValue={true}><Switch defaultChecked /></Form.Item>
      </div>
    </div>
  );

  const contactColumns = [
    { key: 'contactType', title: 'Type', type: 'select', options: CONTACT_TYPE_OPTIONS, width: 130 },
    { key: 'personName', title: 'Name', type: 'text', width: 160 },
    { key: 'mobile', title: 'Mobile', type: 'text', width: 120 },
    { key: 'email', title: 'Email', type: 'text', width: 180 },
    { key: 'city', title: 'City', type: 'text', width: 120 },
    { key: 'state', title: 'State', type: 'text', width: 120 },
    { key: 'isPrimary', title: 'Primary', type: 'switch', width: 80 }
  ];
  const addressColumns = [
    { key: 'addressType', title: 'Type', type: 'select', options: ADDRESS_TYPE_OPTIONS, width: 140 },
    { key: 'line1', title: 'Line 1', type: 'text', width: 200 },
    { key: 'line2', title: 'Line 2', type: 'text', width: 200 },
    { key: 'city', title: 'City', type: 'text', width: 120 },
    { key: 'state', title: 'State', type: 'text', width: 120 },
    { key: 'pincode', title: 'Pincode', type: 'text', width: 100 },
    { key: 'active', title: 'Active', type: 'switch', width: 74 }
  ];
  const documentColumns = [
    { key: 'documentType', title: 'Type', type: 'select', options: DOCUMENT_TYPE_OPTIONS, width: 160 },
    { key: 'documentNumber', title: 'Number', type: 'text', width: 160 },
    { key: 'filePath', title: 'File (URL/path)', type: 'text', width: 220 },
    { key: 'expiryDate', title: 'Expiry', type: 'date', width: 150 },
    { key: 'remarks', title: 'Remarks', type: 'text', width: 200 },
    { key: 'active', title: 'Active', type: 'switch', width: 74 }
  ];

  const contactsTab = (
    <div>
      <div style={grid2}>
        <Form.Item name="officialMobile" label="Official Mobile" rules={[mobileRule]}><Input maxLength={15} /></Form.Item>
        <Form.Item name="officialEmail" label="Official Email" rules={[emailRule]}><Input maxLength={160} /></Form.Item>
        <Form.Item name="emergencyContactName" label="Emergency Contact Name"><Input maxLength={160} /></Form.Item>
        <Form.Item name="emergencyContactMobile" label="Emergency Contact Mobile" rules={[mobileRule]}><Input maxLength={15} /></Form.Item>
      </div>
      <Divider style={{ margin: '4px 0 10px' }} orientation="left" plain>Contacts</Divider>
      {hasId ? <EmployeeSubGrid employeeId={editing.id} api={hrEmployeesApi.contacts} columns={contactColumns}
        makeBlank={() => ({ contactType: 'OTHER', isPrimary: false })} emptyText="No contacts yet." /> : saveFirst}
      <Divider style={{ margin: '14px 0 10px' }} orientation="left" plain>Addresses (Permanent / Communication / Office)</Divider>
      {hasId ? <EmployeeSubGrid employeeId={editing.id} api={hrEmployeesApi.addresses} columns={addressColumns}
        makeBlank={() => ({ addressType: 'PERMANENT', active: true })} emptyText="No addresses yet." /> : null}
    </div>
  );

  const documentsTab = hasId
    ? <EmployeeSubGrid employeeId={editing.id} api={hrEmployeesApi.documents} columns={documentColumns}
        makeBlank={() => ({ documentType: 'AADHAAR', active: true })} emptyText="No documents yet." />
    : saveFirst;

  const loginTab = (
    <div style={grid2}>
      <Form.Item name="userId" label="Linked User ID (optional)" tooltip="public.users(id) if this employee also has a login."><Input maxLength={64} placeholder="UUID (optional)" /></Form.Item>
      <Form.Item label="Last Login"><Input value={editing && editing.lastLoginAt ? dayjs(editing.lastLoginAt).format('DD-MM-YYYY HH:mm') : '—'} disabled /></Form.Item>
      <Form.Item name="canLogin" label="Can Login" valuePropName="checked" tooltip="Capability flag for a future employee login."><Switch /></Form.Item>
      <Form.Item name="loginEnabled" label="Login Enabled" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item name="loginLocked" label="Login Locked" valuePropName="checked"><Switch /></Form.Item>
    </div>
  );

  const managerTab = (
    <div style={grid2}>
      <Form.Item name="reportsToEmployeeId" label="Reports To (Manager)" tooltip="Must be an active employee; cannot be self.">
        <Select allowClear showSearch optionFilterProp="label"
          options={managerOpts.filter((o) => !editing || o.value !== editing.id)} placeholder="Select manager…" />
      </Form.Item>
    </div>
  );

  const branchTab = (
    <div style={grid2}>
      <Form.Item name="branchId" label="Branch"><Select allowClear showSearch optionFilterProp="label" options={branches} placeholder="Select branch…" /></Form.Item>
    </div>
  );

  const HISTORY_COLS = [
    { title: 'Customer Code', dataIndex: 'customerCode', width: 130 },
    { title: 'Customer', dataIndex: 'customerName', width: 200 },
    { title: 'Assigned', dataIndex: 'assignedAt', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '—' },
    { title: 'Removed', dataIndex: 'removedAt', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '—' },
    { title: 'Current', dataIndex: 'isCurrent', width: 90, align: 'center', render: (v) => v ? <Tag color="green">CURRENT</Tag> : <Tag>past</Tag> },
    { title: 'Reason', dataIndex: 'reason', render: (v) => v || '—' }
  ];
  const historyTab = hasId ? <HistoryPanel employeeId={editing.id} columns={HISTORY_COLS} /> : saveFirst;

  const tabItems = [
    { key: 'general', label: 'General', children: generalTab },
    { key: 'employment', label: 'Employment', children: employmentTab },
    { key: 'sales', label: 'Sales', children: salesTab },
    { key: 'contacts', label: 'Contacts', children: contactsTab },
    { key: 'documents', label: 'Documents', children: documentsTab },
    { key: 'login', label: 'Login', children: loginTab },
    { key: 'manager', label: 'Manager', children: managerTab },
    { key: 'branch', label: 'Branch', children: branchTab },
    { key: 'history', label: 'History', children: historyTab }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><TeamOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Employees</span>
            <span className="hr-headstrip__subtitle">Enterprise employee master — branch, manager, sales config, documents, contacts & history.</span>
          </div>
        </div>
        <Space size={6}><span className="hr-stat-chip"><strong>{total}</strong> total</span></Space>
      </div>

      <div className="hr-toolbar">
        <Input allowClear size="small" prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search code, name, mobile, email…" value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }} style={{ width: 300 }} />
        <Select size="small" allowClear placeholder="Status" value={status || undefined} style={{ width: 150 }}
          options={STATUS_OPTIONS} onChange={(v) => { setPage(1); setStatus(v || null); }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Employee</Button>
      </div>

      <div className="hr-card">
        <Spin spinning={loading}>
          <Table className="hr-grid" size="small" rowKey="id" dataSource={rows} columns={columns}
            scroll={{ x: 'max-content' }} sticky
            pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, onChange: (p) => setPage(p), size: 'small' }}
            locale={{ emptyText: 'No employees yet. Click Add Employee to start.' }} />
        </Spin>
      </div>

      <Modal open={modalOpen} title={editing && editing.id ? `Edit Employee — ${editing.employeeName || ''}` : 'Add Employee'}
        onCancel={() => setModalOpen(false)} onOk={submit} confirmLoading={saving}
        okText={editing && editing.id ? 'Save' : 'Create'} width={880} destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
        <Spin spinning={loadingOne}>
          <Form form={form} layout="vertical" size="small" style={{ marginTop: 4 }}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" items={tabItems} />
          </Form>
        </Spin>
      </Modal>

      {/* HRMS-3 — provisioning + change-history modals (additive) */}
      <EmployeeProvisioningModal employeeId={provisionFor} open={!!provisionFor} onClose={() => setProvisionFor(null)} />
      <ChangeHistoryModal record={changeHistoryFor} onClose={() => setChangeHistoryFor(null)} />
    </div>
  );
}

// HRMS-3 — append-only change history for one employee (transfers,
// status, login & salesperson lifecycle).
function ChangeHistoryModal({ record, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!record) return;
    let alive = true; setLoading(true);
    hrEmployeesApi.history(record.id, { limit: 200 })
      .then((out) => { if (alive) setRows((out && out.rows) || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [record]);
  const columns = [
    { title: 'When', dataIndex: 'createdAt', width: 150, render: (v) => v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '—' },
    { title: 'Event', dataIndex: 'eventType', width: 170, render: (v) => <Tag color={eventTagColor(v)}>{eventLabel(v)}</Tag> },
    { title: 'Old', dataIndex: 'oldValue', render: (v) => formatSnapshot(v) },
    { title: 'New', dataIndex: 'newValue', render: (v) => formatSnapshot(v) }
  ];
  return (
    <Modal open={!!record} title={record ? `History — ${record.employeeName || ''}` : 'History'}
      onCancel={onClose} footer={null} width={760} destroyOnClose>
      <Table className="hr-grid" size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns}
        pagination={{ pageSize: 10, size: 'small' }} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No change history yet for this employee.' }} />
    </Modal>
  );
}

// Read-only salesperson assignment history for an employee.
function HistoryPanel({ employeeId, columns }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    hrEmployeesApi.salespersonHistory(employeeId)
      .then((out) => { if (alive) setRows((out && out.rows) || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [employeeId]);
  return (
    <Table className="hr-grid" size="small" rowKey="id" loading={loading} dataSource={rows} columns={columns}
      pagination={false} scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No customer assignments recorded for this employee yet.' }} />
  );
}
